import * as React from "react";
import {
  cls,
  EntityRow,
  OutlookHeader,
  RecipientRow,
  SectionLabel,
} from "./components";
import { CONN_TABS, ConnTab, matchesTab, PRIMARY_TAB_KINDS } from "./connTabs";
import { Icon } from "./icons";
import type { Connection, ContactEntity, EmailContext } from "./types";

/**
 * Initial number of TO + CC recipients shown before the "Show more"
 * toggle. The From row is always rendered on top of this — it's never
 * counted against the limit.
 */
const RECIPIENTS_INITIAL_LIMIT = 5;

/** Label + initial Add-Connection tab keyed off the active filter tab. */
function addButtonForTab(tab: ConnTab): { label: string; tab: ConnTab } {
  switch (tab) {
    case "case": return { label: "Add case", tab: "case" };
    case "account": return { label: "Add account", tab: "account" };
    case "business": return { label: "Add business", tab: "business" };
    case "other": return { label: "Add record", tab: "other" };
    case "all":
    default:
      return { label: "Add connection", tab: "all" };
  }
}

export function MainPane({
  email,
  connections,
  emailInEpic,
  importing,
  importError,
  onImportEmail,
  justAddedId,
  onAddConnection,
  onOpenConnection,
  onOpenContact,
  onShowToast,
}: {
  email: EmailContext;
  /** null = still loading, [] = loaded with nothing, [...] = rows. */
  connections: Connection[] | null;
  /**
   * True only after we've confirmed the EmailMessage row exists in EPIC.
   * When false (and not loading), the Connections section is replaced by
   * an import prompt (see ImportPrompt) — pull the message into EPIC first.
   */
  emailInEpic: boolean;
  /** Import-in-flight (drives the spinner) + the server's failure reason. */
  importing: boolean;
  importError: string | null;
  /** Kick off reimport_email_from_outlook for the open message (App owns it). */
  onImportEmail: () => void;
  justAddedId?: string | null;
  /**
   * Open the Add Connection flow. The optional `initialKind` is set
   * from the active filter tab so clicking "Add account" lands on the
   * Add screen with the Account chip pre-selected.
   */
  onAddConnection: (initialTab?: ConnTab) => void;
  onOpenConnection: (id: string) => void;
  onOpenContact: (id: string) => void;
  onShowToast?: (msg: string) => void;
}): JSX.Element {
  const connectionsLoading = connections === null;
  const connectionsList = connections ?? [];

  // Combined people count — sender + every recipient. Mirrors what the
  // user actually sees in the merged section.
  const allPeople = [email.from, ...email.to, ...email.cc];
  const peopleInEpic = allPeople.filter((c) => c.inEpic).length;
  const peopleTotal = allPeople.length;

  // TO first, then CC — single ordered list so the "first 5" cut-off is
  // predictable across emails. Truncation never includes the From row;
  // that's always rendered separately at the top.
  const allRecipients: Array<{ contact: ContactEntity; role: "TO" | "CC" }> = [
    ...email.to.map((c) => ({ contact: c, role: "TO" as const })),
    ...email.cc.map((c) => ({ contact: c, role: "CC" as const })),
  ];
  // Reset expanded state whenever the open email changes — useMemo on
  // itemId would also work, but a key-based reset is simpler.
  const [expanded, setExpanded] = React.useState(false);
  React.useEffect(() => setExpanded(false), [email.itemId]);

  const visibleRecipients = expanded
    ? allRecipients
    : allRecipients.slice(0, RECIPIENTS_INITIAL_LIMIT);
  const hiddenCount = allRecipients.length - visibleRecipients.length;

  const sorted = [...connectionsList].sort((a, b) => {
    // High-priority OPEN cases bubble to the top of the list (closed
    // cases keep their natural linked-time position regardless of
    // priority).
    const aHigh =
      a.entity.kind === "case" &&
      a.entity.priority === "high" &&
      a.entity.status !== "closed"
        ? 1 : 0;
    const bHigh =
      b.entity.kind === "case" &&
      b.entity.priority === "high" &&
      b.entity.status !== "closed"
        ? 1 : 0;
    if (aHigh !== bHigh) return bHigh - aHigh;
    const aOverdue = a.entity.kind === "task" && a.entity.overdue ? 1 : 0;
    const bOverdue = b.entity.kind === "task" && b.entity.overdue ? 1 : 0;
    if (aOverdue !== bOverdue) return bOverdue - aOverdue;
    return (b.linkedAt || "").localeCompare(a.linkedAt || "");
  });

  // Tab filter state. Reset to "All" when the open email changes so the
  // user always lands on the full list for a fresh email.
  const [activeTab, setActiveTab] = React.useState<ConnTab>("all");
  React.useEffect(() => setActiveTab("all"), [email.itemId]);

  const tabCounts: Record<ConnTab, number> = {
    all: connectionsList.length,
    case: connectionsList.filter((c) => c.entity.kind === "case").length,
    account: connectionsList.filter((c) => c.entity.kind === "account").length,
    business: connectionsList.filter((c) => c.entity.kind === "business").length,
    other: connectionsList.filter((c) => !PRIMARY_TAB_KINDS.includes(c.entity.kind)).length,
  };

  const visibleConnections = sorted.filter((c) => matchesTab(c.entity.kind, activeTab));

  return (
    <>
      <OutlookHeader
        leadingKind="none"
        title={email.subject}
        trailing={
          <MoreMenu email={email} onShowToast={onShowToast} />
        }
      />
      <div className="pane">
        <SectionLabel
          title="People"
          count={`${peopleInEpic} of ${peopleTotal} in EPIC`}
        />
        <div className="card">
          <RecipientRow
            contact={email.from}
            role="FROM"
            onClick={() => onOpenContact(email.from.id)}
          />
          {visibleRecipients.map(({ contact, role }) => (
            <RecipientRow
              key={contact.id}
              contact={contact}
              role={role}
              onClick={() => onOpenContact(contact.id)}
            />
          ))}
          {hiddenCount > 0 && (
            <button
              className="recipients-toggle"
              type="button"
              onClick={() => setExpanded(true)}
            >
              Show {hiddenCount} more
            </button>
          )}
          {expanded && allRecipients.length > RECIPIENTS_INITIAL_LIMIT && (
            <button
              className="recipients-toggle"
              type="button"
              onClick={() => setExpanded(false)}
            >
              Show less
            </button>
          )}
        </div>

        {/* When the email isn't in EPIC yet there's nothing to link to, so
            the Connections section is replaced by an import prompt (pull the
            message in from Exchange). During the initial check we still show
            the loading state so the user has feedback. */}
        {connectionsLoading || emailInEpic ? (
          <>
            <SectionLabel title="Connections" />

            {!connectionsLoading && connectionsList.length > 0 && (
              <div className="connections-tabs" role="tablist">
                {CONN_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    className={cls("connections-tab", activeTab === tab.key && "on")}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <span className="connections-tab-label">{tab.label}</span>
                    <span className="connections-tab-count">{tabCounts[tab.key]}</span>
                  </button>
                ))}
              </div>
            )}

            {!connectionsLoading && (() => {
              const cfg = addButtonForTab(activeTab);
              return (
                <button
                  className="add-connection-btn center-text"
                  onClick={() => onAddConnection(cfg.tab)}
                >
                  <span className="icn"><Icon.Plus /></span>
                  <span className="text">{cfg.label}</span>
                </button>
              );
            })()}

            {connectionsLoading ? (
              <div className="empty">
                <div className="empty-title subtle">Loading connections…</div>
              </div>
            ) : connectionsList.length === 0 ? (
              <div className="empty">
                <div className="empty-emoji" aria-hidden="true">😱</div>
                <div className="empty-title subtle">No connections</div>
              </div>
            ) : visibleConnections.length === 0 ? (
              <div className="empty">
                <div className="empty-title subtle">
                  No {CONN_TABS.find((t) => t.key === activeTab)?.label.toLowerCase()} yet
                </div>
              </div>
            ) : (
              <div className="card">
                {visibleConnections.map((c) => (
                  <EntityRow
                    key={c.id}
                    entity={c.entity}
                    justAdded={c.id === justAddedId}
                    onClick={() => onOpenConnection(c.id)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <ImportPrompt
            importing={importing}
            error={importError}
            onImport={onImportEmail}
          />
        )}
      </div>
    </>
  );
}

/**
 * Shown in place of the Connections section when the open email isn't in
 * EPIC yet. Offers a one-tap import (App calls reimport_email_from_outlook).
 * While the server searches mailboxes the button disables and its label
 * becomes a spinner; on a not-found / failure the card turns red with the
 * server's reason and the button becomes "Try again".
 */
function ImportPrompt({
  importing,
  error,
  onImport,
}: {
  importing: boolean;
  error: string | null;
  onImport: () => void;
}): JSX.Element {
  // A pending retry (importing) supersedes the error treatment.
  const isError = !!error && !importing;
  return (
    <div className={cls("import-card", isError && "is-error")}>
      <div className="import-emoji" aria-hidden="true">
        {isError ? "⚠️" : "📥"}
      </div>
      <div className={cls("import-title", isError && "error")}>
        {isError ? "Import failed" : "Email not in EPIC"}
      </div>
      <div className="import-sub">
        {importing
          ? "Searching your mailboxes for this message…"
          : isError
            ? error
            : "Import it from Exchange to link records, create cases, and see connections."}
      </div>
      <button
        className="import-btn"
        type="button"
        onClick={onImport}
        disabled={importing}
      >
        {importing ? (
          <>
            <span className="confirm-spinner" aria-hidden="true" />
            Importing…
          </>
        ) : isError ? (
          "Try again"
        ) : (
          "Import email"
        )}
      </button>
    </div>
  );
}

/**
 * Copy text to the system clipboard. Tries the modern async API first;
 * falls back to a hidden-textarea + execCommand("copy") because the Office
 * task-pane iframe is loaded without `clipboard-write` permissions policy
 * and `navigator.clipboard.writeText` rejects with NotAllowedError.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    console.error("Copy fallback failed:", e);
    return false;
  }
}

function MoreMenu({
  email,
  onShowToast,
}: {
  email: EmailContext;
  onShowToast?: (msg: string) => void;
}): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const imid = email.internetMessageId;

  const handleCopyImid = async () => {
    setOpen(false);
    if (!imid) return;
    const ok = await copyToClipboard(imid);
    onShowToast?.(ok ? "Internet Message ID copied" : "Copy failed");
  };

  return (
    <div ref={wrapRef} className="more-menu-wrap">
      <button
        className="trailing"
        aria-label="More"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        ···
      </button>
      {open && (
        <div className="more-menu" role="menu">
          <button
            className="more-menu-item"
            disabled={!imid}
            onClick={handleCopyImid}
          >
            Copy Internet Message ID
          </button>
        </div>
      )}
    </div>
  );
}
